import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { AlarmClock, Save, Loader2, Lock, Info } from "lucide-react";

// PROMPT 13P — admin-configurable Document Triage urgency thresholds (warning/urgent days).
export default function DocumentSettings() {
  const [warn, setWarn] = useState("");
  const [urg, setUrg] = useState("");
  const [defaults, setDefaults] = useState({ warning_days: 7, urgent_days: 14 });
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/doc-center/settings/thresholds")
      .then(({ data }) => {
        setWarn(String(data.settings?.warning_days ?? 7));
        setUrg(String(data.settings?.urgent_days ?? 14));
        setDefaults(data.defaults || { warning_days: 7, urgent_days: 14 });
        setCanEdit(!!data.can_edit);
      })
      .catch((e) => toast.error(errMsg(e, "Could not load document settings.")))
      .finally(() => setLoading(false));
  }, []);

  const w = Number(warn), u = Number(urg);
  const invalid = !(Number.isFinite(w) && Number.isFinite(u) && w >= 1 && u >= 2 && w < u);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/doc-center/settings/thresholds", { warning_days: w, urgent_days: u });
      setWarn(String(data.settings.warning_days));
      setUrg(String(data.settings.urgent_days));
      toast.success("Triage thresholds saved.");
    } catch (e) { toast.error(errMsg(e, "Save failed.")); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="max-w-2xl"><Skeleton className="h-56" /></div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="document-settings-page">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#252221]">Document Settings</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Configure how the top-bar Document Triage badge signals backlog urgency.</p>
      </div>

      <Card className="p-6 border-[#E6EDF2]" data-testid="settings-triage-thresholds" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex items-center gap-3 mb-5">
          <span className="h-10 w-10 rounded-xl bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center shrink-0"><AlarmClock size={19} /></span>
          <div>
            <div className="font-display font-semibold text-[#252221]">Triage urgency thresholds</div>
            <div className="text-[13px] text-muted-foreground">The badge turns amber at the warning threshold and red at the urgent threshold, based on the oldest document item.</div>
          </div>
        </div>

        {!canEdit && (
          <div className="flex items-start gap-2 rounded-lg bg-[#F1F5F8] border border-[#D6E0E7] px-3 py-2.5 text-[13px] text-[#4B5563] mb-4" data-testid="settings-triage-readonly">
            <Lock size={15} className="mt-0.5 shrink-0 text-[#9AA5B1]" />
            Only a Super Admin can change these thresholds. You can view the current values.
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[13px] text-[#4B5563]">Warning threshold (days)</Label>
            <Input type="number" min="1" value={warn} disabled={!canEdit}
              onChange={(e) => setWarn(e.target.value)} className="h-10 text-[14px]" data-testid="settings-warning-days" />
          </div>
          <div>
            <Label className="text-[13px] text-[#4B5563]">Urgent threshold (days)</Label>
            <Input type="number" min="2" value={urg} disabled={!canEdit}
              onChange={(e) => setUrg(e.target.value)} className="h-10 text-[14px]" data-testid="settings-urgent-days" />
          </div>
        </div>

        {invalid && (
          <div className="flex items-start gap-2 text-[12.5px] text-[#9C1E22] mt-3" data-testid="settings-threshold-error">
            <Info size={14} className="mt-0.5 shrink-0" />
            Warning must be at least 1 day, urgent at least 2 days, and the warning threshold must be lower than the urgent threshold.
          </div>
        )}

        <div className="text-[12px] text-muted-foreground mt-3">
          Defaults: {defaults.warning_days} days (warning) / {defaults.urgent_days} days (urgent).
        </div>

        {canEdit && (
          <div className="flex justify-end mt-5">
            <Button onClick={save} disabled={saving || invalid} data-testid="settings-documents-save-btn"
              className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 h-11 px-6 rounded-full">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save settings
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
